import { CSVLink } from 'components/CSVLink';
import { countBy } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { PERFORMANCE_FEATURE_ASSESS_ORGANIZATION_OPEN_RESPONSE_QUESTIONS } from 'utils/models/PerformanceUtils';
import LikertHeatmapVisualizations from 'views/Widgets/Charts/LikertHeatmapVisualizations';
import { ICONS } from '../../consts/consts';
import { getFilterDisplayName } from '../../utils/models/Filters';
import { getCampaignFeature } from '../../utils/models/Performance';
import { createCSVFileName, sumObjectValues } from '../../utils/util/util';
import AccordionTable from '../Widgets/AccordionTable';
import TextQuestionTableCard from '../Widgets/Cards/TextQuestionTableCard';
import {
  FORMAT_VALUE,
  FORMAT_VALUE_PERCENTAGES_FAVOURABLE,
  FORMAT_VALUE_PERCENTAGES_NEUTRAL,
  FORMAT_VALUE_PERCENTAGES_UNFAVORABLE,
} from '../Widgets/Charts/LikertHeatmap';
import MultipleChoiceHeatmap from '../Widgets/Charts/MultipleChoiceHeatmap';
import { CARD_TYPES } from '../Widgets/Dashboards/CardRowDashboard';
import { INPUT_TYPES } from '../Widgets/Inputs/ValidatedInputTypes';

const groupResponseDataByPeople = (responses, formatMessage) => {
  if (!responses) {
    return responses;
  }

  return Object.keys(responses).reduce((accFilters, filterName) => {
    accFilters[filterName] = Object.keys(responses[filterName]).reduce(
      (accFilterValues, filterValue) => {
        accFilterValues[filterValue] = {};
        accFilterValues[filterValue]['eligible'] =
          responses[filterName][filterValue]['eligible'];

        const responsesGroupedByPeople = countBy(
          responses[filterName][filterValue]['responses']
        );

        accFilterValues[filterValue]['responses'] = Object.entries(
          responsesGroupedByPeople
        ).map(([personDescription, count]) => {
          return formatMessage(
            {
              id: 'app.views.engagement_survey.engagement_survey.people_editor_response.details',
              defaultMessage:
                '{personDescription} - {count, plural, one {1 nomination} other {# nominations}}',
            },
            {
              personDescription,
              count,
            }
          );
        });

        return accFilterValues;
      },
      {}
    );
    return accFilters;
  }, {});
};

export const getEngagementSurveyComponents = (
  formatMessage,
  campaign,
  data,
  title,
  reportItemId
) => {
  // if we have no data, return an empty array
  if (!data?.length) {
    return [];
  }

  const questions = getCampaignFeature(
    campaign,
    PERFORMANCE_FEATURE_ASSESS_ORGANIZATION_OPEN_RESPONSE_QUESTIONS
  );
  if (!questions || !questions?.length) {
    return [];
  }

  // TODO: Add in additional question types as support is built out
  const TEXT_QUESTION_TYPES = [
    INPUT_TYPES.TEXTAREA,
    INPUT_TYPES.RICH_TEXT_EDITOR,
  ];

  const PEOPLE_EDITOR_QUESTION_TYPES = [INPUT_TYPES.PEOPLE_EDITOR];

  const LIKERT_QUESTION_TYPES = [INPUT_TYPES.LIKERT];
  const MULTIPLE_CHOICE_QUESTION_TYPES = [
    INPUT_TYPES.MULTIPLE_CHOICE,
    INPUT_TYPES.DROPDOWN,
  ];

  const textQuestions = [];
  const likertQuestions = [];
  const multipleChoiceQuestions = [];
  const peopleEditorQuestions = [];

  data.forEach((q) => {
    const qType = q?.configs?.type?.toUpperCase();

    if (TEXT_QUESTION_TYPES.includes(qType)) {
      // @ts-expect-error
      textQuestions.push(q);
    }
    if (LIKERT_QUESTION_TYPES.includes(qType)) {
      // @ts-expect-error
      likertQuestions.push(q);
    }
    if (MULTIPLE_CHOICE_QUESTION_TYPES.includes(qType)) {
      // @ts-expect-error
      multipleChoiceQuestions.push(q);
    }
    if (PEOPLE_EDITOR_QUESTION_TYPES.includes(qType)) {
      // @ts-expect-error
      peopleEditorQuestions.push(q);
    }
  });

  const textQuestionRows = textQuestions.map((q) => ({
    // @ts-expect-error
    name: q?.configs?.name,
    // @ts-expect-error
    displayName: q?.configs?.display_name,
    // @ts-expect-error
    count: q?.all?.responses?.length,
    // @ts-expect-error
    data: { overall: { overall: q?.all }, ...q?.responses },
    // @ts-expect-error
    factor: q?.configs?.factor,
    // @ts-expect-error
    removed_count: q?.all?.removed_count,
  }));

  const peopleEditorQuestionRows = peopleEditorQuestions.map((q) => ({
    // @ts-expect-error
    name: q?.configs?.name,
    // @ts-expect-error
    displayName: q?.configs?.display_name,
    // @ts-expect-error
    count: q?.all?.responses?.length,
    data: groupResponseDataByPeople(
      // @ts-expect-error
      { overall: { overall: q?.all }, ...q?.responses },
      formatMessage
    ),
    // @ts-expect-error
    factor: q?.configs?.factor,
    // @ts-expect-error
    removed_count: q?.all?.removed_count,
  }));

  const multipleChoiceQuestionRows = multipleChoiceQuestions.map((q) => {
    // @ts-expect-error
    const question = questions.find((_q) => _q.name === q?.configs?.name);

    return {
      // @ts-expect-error
      name: q?.configs?.name,
      // @ts-expect-error
      displayName: q?.configs?.display_name,
      // @ts-expect-error
      all: q?.all?.responses,
      // @ts-expect-error
      total: sumObjectValues(q?.all?.responses),
      // @ts-expect-error
      responses: q?.responses,
      options: question.objects || question.options || [],
      // @ts-expect-error
      factor: q?.configs?.factor,
      // @ts-expect-error
      all_by_breakdown: q?._all_by_breakdown,
    };
  });

  const csvFormatTextQuestion = (q, noFactorHeader: string) => {
    const headerRow = [
      `Question: ${q?.displayName}`,
      q.factor ?? q.configs?.factor ?? noFactorHeader,
      'Field',
      'Value',
      '',
      '',
      '',
      '',
      '',
      '',
      'Response',
    ];

    const groupings = Object.keys(q?.data || {});

    const dataRows = [];

    for (const group of groupings) {
      const groupNames = Object.keys(q?.data?.[group] || {});
      for (const groupName of groupNames) {
        for (const response of q?.data?.[group]?.[groupName]?.responses || []) {
          // @ts-expect-error
          dataRows.push([
            '',
            '',
            getFilterDisplayName(group, formatMessage),
            groupName,
            '',
            '',
            '',
            '',
            '',
            '',
            response.replace(/"/g, '""'),
          ]);
        }
      }
    }

    const result = [headerRow, ...dataRows];

    result.push(['']);
    return result;
  };

  const csvFormatNonTextQuestion = (
    q,
    options,
    includeAverage,
    noFactorText
  ) => {
    const headerRow = [
      `Likert: ${q?.configs?.display_name || q?.displayName}`,
      q.factor ?? q.configs?.factor ?? noFactorText,
      'Field',
      'Value',
      ...(includeAverage
        ? ['Average', '% Favorable', '% Unfavorable', '% Neutral']
        : []),
      ...options.map((o) => o.toString()),
    ];
    const groupings = Object.keys(q?.responses || {});
    const dataRows = [];
    for (const group of groupings) {
      const groupNames = Object.keys(q?.responses?.[group] || {});
      for (const groupName of groupNames) {
        const average = FORMAT_VALUE(
          q?.responses?.[group]?.[groupName]?.responses
        );
        const favorable = FORMAT_VALUE_PERCENTAGES_FAVOURABLE(
          q?.responses?.[group]?.[groupName]?.responses
        );
        const unfavorable = FORMAT_VALUE_PERCENTAGES_UNFAVORABLE(
          q?.responses?.[group]?.[groupName]?.responses
        );
        const neutral = FORMAT_VALUE_PERCENTAGES_NEUTRAL(
          q?.responses?.[group]?.[groupName]?.responses
        );
        // @ts-expect-error
        dataRows.push([
          '',
          '',
          getFilterDisplayName(group, formatMessage),
          groupName,
          ...(includeAverage ? [average, favorable, unfavorable, neutral] : []),
          ...options.map(
            (o) => q?.responses?.[group]?.[groupName]?.responses?.[o] || 0
          ),
        ]);
      }
    }

    const result = [headerRow, ...dataRows];

    result.push(['']);
    return result;
  };

  const createCsvData = () => {
    let result = [];

    // Assumption that likert only spans from 1-5
    const LIKERT_SCALE = [5, 4, 3, 2, 1];

    const noFactorHeader = formatMessage({
      id: 'app.views.engagement_survey.create_csv_data.no_factor',
      defaultMessage: '(No factor)',
    });

    likertQuestions.forEach((e) => {
      // @ts-expect-error
      result = [
        ...result,
        ...csvFormatNonTextQuestion(e, LIKERT_SCALE, true, noFactorHeader),
      ];
    });

    multipleChoiceQuestionRows.forEach((e) => {
      // @ts-expect-error
      result = [
        ...result,
        ...csvFormatNonTextQuestion(
          e,
          e.options.map((o) => o.id),
          false,
          noFactorHeader
        ),
      ];
    });

    textQuestionRows.forEach((e) => {
      // @ts-expect-error
      result = [...result, ...csvFormatTextQuestion(e, noFactorHeader)];
    });

    peopleEditorQuestionRows.forEach((e) => {
      // @ts-expect-error
      result = [...result, ...csvFormatTextQuestion(e, noFactorHeader)];
    });

    return result;
  };

  const exportButton = (
    <CSVLink
      className="btn btn-light btn-sm"
      filename={createCSVFileName()}
      data={createCsvData()}
    >
      <i className={ICONS.EXPORT + ' me-2'} />
      <FormattedMessage
        id="app.views.engagement_survey.engagement_survey.csv_link.export"
        defaultMessage="
      Export
    "
      />
    </CSVLink>
  );

  const renderItem = (o) => {
    return getFilterDisplayName(o.id, formatMessage);
  };

  return [
    {
      type: CARD_TYPES.MULTICHART,
      title: (
        <div className="d-flex justify-content-between">
          <span className="align-self-center">
            {title ??
              formatMessage({
                id: 'app.views.engagement_survey.engagement_survey.responses',
                defaultMessage: 'Responses',
              })}
          </span>
          {exportButton}
        </div>
      ),
      emptyStateText: formatMessage({
        id: 'app.views.engagement_survey.engagement_survey.no_responses',
        defaultMessage: 'No responses are currently available',
      }),
      content: [
        ...(likertQuestions.length
          ? [
              <AccordionTable
                key={0}
                columns={[
                  // @ts-expect-error
                  {
                    name: formatMessage({
                      id: 'app.views.engagement_survey.engagement_survey.likert_questions',
                      defaultMessage: 'Likert questions',
                    }),
                  },
                ]}
                headerClassname="py-1 px-3 es-accordion-table-header"
                customContent={
                  <LikertHeatmapVisualizations
                    data={likertQuestions}
                    reportItemId={reportItemId}
                    // @ts-expect-error
                    renderItem={renderItem}
                  />
                }
              />,
            ]
          : []),
        ...(multipleChoiceQuestions.length
          ? [
              <AccordionTable
                key={1}
                columns={[
                  // @ts-expect-error
                  {
                    name: formatMessage({
                      id: 'app.views.engagement_survey.engagement_survey.multiple_choice_questions',
                      defaultMessage: 'Multiple choice questions',
                    }),
                  },
                ]}
                headerClassname="py-1 px-3 es-accordion-table-header"
                // process questions sorted by factor, with the empty factor at the end
                customContent={
                  <MultipleChoiceHeatmap data={multipleChoiceQuestionRows} />
                }
              />,
            ]
          : []),
        ...(textQuestions.length
          ? [
              <TextQuestionTableCard
                title={formatMessage({
                  id: 'app.views.widgets.cards.text_question_table_card.text_questions',
                  defaultMessage: 'Text Questions',
                })}
                key={2}
                rows={textQuestionRows}
                exportButton={exportButton}
                renderItem={renderItem}
              />,
            ]
          : []),
        ...(peopleEditorQuestions.length
          ? [
              <TextQuestionTableCard
                title={formatMessage({
                  id: 'app.views.widgets.cards.text_question_table_card.people_question',
                  defaultMessage: 'People questions',
                })}
                key={3}
                rows={peopleEditorQuestionRows}
                exportButton={exportButton}
                renderItem={renderItem}
              />,
            ]
          : []),
      ],
    },
  ];
};
